import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

/**
 * Collections — `/api/admin/collections` (ff-api admin.collection).
 *   GET  (no owner)               → { collections, total, page, page_size }  (all, paginated)
 *   GET  ?search=&page=&page_size= → filtered list of all collections
 *   GET  ?uid= | ?oid=            → { collections, count, total }            (owner-scoped)
 *   GET  /<ecolid>/entities       → { entities, ... }
 *   POST  { uid|oid, name, ... }  · PUT /<ecolid>  · DELETE /<ecolid>
 *   POST /<ecolid>/entities { eid } · DELETE /<ecolid>/entities/<eid>
 */

const BASE = '/api/admin/collections';

export interface AdminCollection {
  [key: string]: any;
  ecolid?: number;
  name?: string;
  description?: string | null;
  private?: number | boolean | null;
  color?: string | null;
  icon?: string | null;
  uid?: number | null;
  oid?: number | null;
}

const buildQs = (params: Record<string, unknown>) => {
  const clean = Object.entries(params).reduce<Record<string, string>>((acc, [k, v]) => {
    if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
    return acc;
  }, {});
  const qs = new URLSearchParams(clean).toString();
  return qs ? `?${qs}` : '';
};

export const collectionKeys = {
  all: ['admin-collections'] as const,
  list: (params: object) => [...collectionKeys.all, 'list', params] as const,
  entities: (ecolid: number, params: object) =>
    [...collectionKeys.all, 'entities', ecolid, params] as const,
};

// ─── Queries ─────────────────────────────────────────────────────────────────

export interface CollectionsListResponse {
  collections: AdminCollection[];
  count: number;
  total: number;
  page?: number;
  page_size?: number;
}

/**
 * List collections. With no `uid`/`oid` it lists ALL collections (paginated,
 * newest-first) and honors `search`; pass `uid`/`oid` to scope to one owner.
 */
export const useCollectionsQuery = (
  params: {
    uid?: number;
    oid?: number;
    search?: string;
    page?: number;
    page_size?: number;
  } = {},
  enabled = true
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: collectionKeys.list(params),
    enabled,
    queryFn: withErrorHandling(
      () => request<CollectionsListResponse>(`${BASE}${buildQs(params)}`),
      'Failed to load collections'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useCollectionEntitiesQuery = (
  ecolid: number | null,
  params: { page?: number; per_page?: number } = {},
  enabled = true
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: collectionKeys.entities(ecolid ?? -1, params),
    enabled: enabled && !!ecolid,
    queryFn: withErrorHandling(
      () => request<any>(`${BASE}/${ecolid}/entities${buildQs(params)}`),
      'Failed to load collection listings'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

// ─── Mutations ───────────────────────────────────────────────────────────────

const useInvalidate = () => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: collectionKeys.all });
};

export const useCreateCollectionMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (body: {
        uid?: number;
        oid?: number;
        name: string;
        description?: string;
        private?: 0 | 1;
        color?: string;
        icon?: string;
      }) => request<any>(BASE, 'POST', body),
      'Failed to create the collection'
    ),
    onSuccess: invalidate,
  });
};

export const useUpdateCollectionMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { ecolid: number; fields: Record<string, unknown> }) =>
        request<any>(`${BASE}/${vars.ecolid}`, 'PUT', vars.fields),
      'Failed to update the collection'
    ),
    onSuccess: invalidate,
  });
};

export const useDeleteCollectionMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (ecolid: number) => request<any>(`${BASE}/${ecolid}`, 'DELETE'),
      'Failed to delete the collection'
    ),
    onSuccess: invalidate,
  });
};

export const useAddEntityToCollectionMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { ecolid: number; eid: number; uid?: number; oid?: number }) =>
        request<any>(`${BASE}/${vars.ecolid}/entities`, 'POST', {
          eid: vars.eid,
          ...(vars.uid != null ? { uid: vars.uid } : {}),
          ...(vars.oid != null ? { oid: vars.oid } : {}),
        }),
      'Failed to add the listing'
    ),
    onSuccess: invalidate,
  });
};

export const useRemoveEntityFromCollectionMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { ecolid: number; eid: number }) =>
        request<any>(`${BASE}/${vars.ecolid}/entities/${vars.eid}`, 'DELETE'),
      'Failed to remove the listing'
    ),
    onSuccess: invalidate,
  });
};
